import React from 'react';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  Container,
} from '@mui/material';
import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import { Newspaper } from 'lucide-react';

// Modern color palette (consistent with Dashboard and ViewTopic)
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components (consistent with other components)
const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
    '& svg': {
      transform: 'translateX(6px)',
    },
  },
}));

const Admin = () => {
  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 5,
            p: 4,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700' }}>
            Admin Management
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            Manage subjects, school levels, topics, and users in the portal.
          </Typography>
        </Box>

        {/* Alert */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={12}>
            <Alert severity="info">
              This is the Admin Management module. Only Admin roles can access this module.
            </Alert>
          </Grid>
        </Grid>

        {/* Cards */}
        <Grid container spacing={3}>
          {[
            {
              title: 'Subjects',
              icon: <MenuBookIcon sx={{ mr: 1, fontSize: 30 }} />,
              description: 'View, add, and edit subjects available in the portal.',
              link: '/subjects',
            },
            {
              title: 'School Levels',
              icon: <LocationCityIcon sx={{ mr: 1, fontSize: 30 }} />,
              description: 'View, add, and edit school levels available in the portal.',
              link: '/schoollevels',
            },
            {
              title: 'Topics',
              icon: <CastForEducationIcon sx={{ mr: 1, fontSize: 30 }} />,
              description: 'View, add, and edit topics available in the portal.',
              link: '/topics',
            },
            {
              title: 'Past Papers',
              icon: <StickyNote2Icon sx={{ mr: 1, fontSize: 30 }} />,
              description: 'View, add, and edit past papers available in the portal.',
              link: '/pastpapers',
            },
            {
              title: 'Revision Hub',
              icon: <AutoStoriesIcon sx={{ mr: 1, fontSize: 30 }} />,
              description: 'View, add, and edit revision papers available in the portal.',
              link: '/revisionhub',
            },
            {
              title: 'Users',
              icon: <PeopleAltIcon sx={{ mr: 1, fontSize: 30 }} />,
              description: 'View, add, and edit users registered in the portal.',
              link: '/adminusers',
            },
            {
              title: 'News Letters',
              icon: <Newspaper sx={{ mr: 1, fontSize: 30 }} />,
              description: 'View, add, and edit news letterd and subscribed users.',
              link: '/newsletters',
            },
          ].map((item, index) => (
            <Grid key={index} size={{ xs: 12, md: 6, lg: 3 }}>
              <ModernCard>
                <CardContent>
                  <Typography
                    gutterBottom
                    sx={{ fontSize: 24, fontWeight: '600', color: colors.secondary, display: 'flex', alignItems: 'center' }}
                  >
                    {item.icon}
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    {item.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Link to={item.link} style={{ textDecoration: 'none' }}>
                    <AnimatedButton
                      variant="outlined"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                    >
                      View {item.title}
                    </AnimatedButton>
                  </Link>
                </CardActions>
              </ModernCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </div>
  );
};

export default Admin;